import React, { useState, useEffect } from "react";
import {
    FaEdit,
    FaCheck,
    FaTimes,
    FaCheckCircle,
    FaExclamationTriangle,
    FaPowerOff,
    FaFan,
    FaThermometerHalf,
    FaBolt,
    FaShieldAlt,
} from "react-icons/fa";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Spinner,
    Input,
    CardFooter,
    FormGroup,
    Alert,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    Badge,
} from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PerformanceTrend from "./PerformanceTrend";
import axiosInstance from "../../components/AxiosInstance";
import CompressorTables2 from "./ChillerTable2";
import InputPressureChart from "./InputPressureChart";

const API_BASE_URL = "https://api.hvacsync.in/api";

// ─── Common Value Card ────────────────────────────────────────────────────────
const CommonValueCard = ({ label, value }) => (
    <Col xs={6} sm={6} md={3} className="mb-3">
        <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: "10px", background: "#f8f9fa" }}
        >
            <CardBody className="py-3 px-3">
                <p
                    className="text-muted mb-1"
                    style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                    }}
                >
                    {label}
                </p>
                <h5 className="fw-bold mb-0" style={{ color: "#2c3e50" }}>
                    {value}
                    <span
                        className="text-muted ms-1"
                        style={{ fontSize: "13px", fontWeight: 400 }}
                    >
                        °C
                    </span>
                </h5>
            </CardBody>
        </Card>
    </Col>
);

// ─── Coil Status Helpers ──────────────────────────────────────────────────────

const COIL_GROUPS = [
    {
        label: "Compressors",
        icon: FaBolt,
        color: "#0d6efd",
        bg: "#e7f0ff",
        keys: ["COMP 1", "COMP 2", "COMP 3", "COMP 4"],
    },
    {
        label: "Motor Protection",
        icon: FaShieldAlt,
        color: "#dc3545",
        bg: "#fdecea",
        keys: ["MP1 FAULT", "MP2 FAULT", "MP3 FAULT", "MP4 FAULT"],
    },
    {
        label: "Condenser Fans",
        icon: FaFan,
        color: "#0dcaf0",
        bg: "#e3f9fd",
        keys: ["COND FAN 1", "COND FAN 2", "FAN OVERLOAD 1/2", "FAN OVERLOAD 3/4"],
    },
    {
        label: "Alarms",
        icon: FaExclamationTriangle,
        color: "#fd7e14",
        bg: "#fff4e5",
        keys: ["HP COMP1 ALARM", "HP COMP2 ALARM", "LP COMP1 ALARM", "LP COMP2 ALARM"],
    },
    {
        label: "Probes & Sensors",
        icon: FaThermometerHalf,
        color: "#6f42c1",
        bg: "#f1edfa",
        keys: ["SUPPLY TEMP PROBE", "AMBIENT TEMP PROBE"],
    },
    {
        label: "System",
        icon: FaPowerOff,
        color: "#198754",
        bg: "#eaf6ef",
        keys: [
            "SEL MODE",
            "SUPERVISORY",
            "EVAP FAN",
            "PHASE FAULT",
            "BLOWER FAULT",
            "DOOR SENSOR FAULT",
        ],
    },
];

// "COMP 1" → "Comp 1", "HP COMP1 ALARM" → "Hp Comp1 Alarm"
const prettyLabel = (key) =>
    key
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");

// Is the value "active"?
const isActive = (val) =>
    String(val).toUpperCase() === "ON" ||
    val === true ||
    val === 1 ||
    val === "1";

// Is this key a fault/alarm type?
const isFaultKey = (key) => /(FAULT|ALARM|OVERLOAD|PROBE|SENSOR)/.test(key.toUpperCase());

// ─── Coil Status Badge ────────────────────────────────────────────────────────
const CoilStatusBadge = ({ label, value, keyName }) => {
    const on = isActive(value);
    const fault = isFaultKey(keyName);

    let badgeColor, textColor, borderColor, icon;

    if (!on) {
        badgeColor = "#f0f2f5";
        textColor = "#adb5bd";
        borderColor = "#e2e8f0";
        icon = null;
    } else if (fault) {
        badgeColor = "#fdecea";
        textColor = "#dc3545";
        borderColor = "#f5c6cb";
        icon = <FaExclamationTriangle size={9} className="me-1" />;
    } else {
        badgeColor = "#eaf6ef";
        textColor = "#198754";
        borderColor = "#b7dfca";
        icon = <FaCheckCircle size={9} className="me-1" />;
    }

    return (
        <div
            className="d-inline-flex align-items-center"
            style={{
                background: badgeColor,
                color: textColor,
                borderRadius: "6px",
                padding: "4px 9px",
                fontSize: "11px",
                fontWeight: 600,
                margin: "3px",
                border: `1px solid ${borderColor}`,
                transition: "all 0.2s",
                whiteSpace: "nowrap",
            }}
        >
            {icon}
            {label}
            <span
                style={{
                    marginLeft: 5,
                    fontWeight: 700,
                    fontSize: "10px",
                    opacity: 0.85,
                }}
            >
                {String(value).toUpperCase()}
            </span>
        </div>
    );
};

// ─── Coil Status Section ──────────────────────────────────────────────────────
const CoilStatusSection = ({ coilStatus, date, time, loading }) => {
    if (loading) {
        return (
            <div className="text-center py-3">
                <Spinner size="sm" color="secondary" />
                <span className="ms-2 text-muted" style={{ fontSize: "13px" }}>
                    Loading coil status…
                </span>
            </div>
        );
    }
    if (!coilStatus) return null;

    return (
        <div className="mb-4">
            {/* Section header */}
            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-1">
                <h6
                    className="fw-semibold mb-0"
                    style={{
                        fontSize: "12px",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: "#6c757d",
                    }}
                >
                    Coil Register Status
                </h6>
                {date && (
                    <small className="text-muted" style={{ fontSize: "11px" }}>
                        {date} · {time}
                    </small>
                )}
            </div>

            <Row className="g-3">
                {COIL_GROUPS.map(({ label, icon: Icon, color, bg, keys }) => {
                    const relevantKeys = keys.filter((k) => k in coilStatus);
                    if (relevantKeys.length === 0) return null;

                    const hasActiveAlarm = relevantKeys.some(
                        (k) => isActive(coilStatus[k]) && isFaultKey(k)
                    );

                    return (
                        <Col xs={12} sm={6} md={4} key={label}>
                            <Card
                                className="border-0 shadow-sm h-100"
                                style={{
                                    borderRadius: "10px",
                                    borderLeft: `3px solid ${
                                        hasActiveAlarm ? "#dc3545" : color
                                    }`,
                                }}
                            >
                                <CardBody className="py-3 px-3">
                                    {/* Group header */}
                                    <div className="d-flex align-items-center mb-2 gap-2">
                                        <div
                                            style={{
                                                width: 30,
                                                height: 30,
                                                borderRadius: "8px",
                                                background: bg,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Icon
                                                size={14}
                                                color={
                                                    hasActiveAlarm ? "#dc3545" : color
                                                }
                                            />
                                        </div>
                                        <span
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: 700,
                                                color: "#2c3e50",
                                            }}
                                        >
                                            {label}
                                        </span>
                                        {hasActiveAlarm && (
                                            <Badge
                                                color="danger"
                                                pill
                                                style={{
                                                    fontSize: "10px",
                                                    marginLeft: "auto",
                                                }}
                                            >
                                                Alert
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Badges */}
                                    <div className="d-flex flex-wrap">
                                        {relevantKeys.map((k) => (
                                            <CoilStatusBadge
                                                key={k}
                                                keyName={k}
                                                label={prettyLabel(k)}
                                                value={coilStatus[k]}
                                            />
                                        ))}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ChillerDetail = () => {
    const navigate = useNavigate();
    const { chillerId } = useParams();
    const location = useLocation();
    const machineId = location.state?.id;

    // ── State ────────────────────────────────────────────────────────────────
    const [telemetry, setTelemetry] = useState(null);
    const [compressors, setCompressors] = useState([]);
    const [loadingCompressors, setLoadingCompressors] = useState(true);
    const [statusMessage, setStatusMessage] = useState({ type: "", message: "" });
    const [lastUpdated, setLastUpdated] = useState(null);

    const [coilData, setCoilData] = useState(null);
    const [loadingCoil, setLoadingCoil] = useState(true);

    const [modalOpen, setModalOpen] = useState(false);
    const [currentCompressor, setCurrentCompressor] = useState(null);
    const [newSetpoint, setNewSetpoint] = useState("");
    const [isUpdatingSetpoint, setIsUpdatingSetpoint] = useState(false);

    // ── Fetch telemetry ──────────────────────────────────────────────────────
    const fetchTelemetry = async () => {
        if (!machineId) {
            setStatusMessage({
                type: "warning",
                message: "Machine ID is missing. Cannot fetch telemetry.",
            });
            setLoadingCompressors(false);
            return;
        }
        setLoadingCompressors(true);
        setStatusMessage({});
        try {
            const res = await axiosInstance.get(
                `${API_BASE_URL}/hvac-machine-telemetry/${machineId}/`
            );
            const data = res.data;
            setTelemetry(data);
            setCompressors(Array.isArray(data.compressors) ? data.compressors : []);
            setLastUpdated(new Date());
        } catch (err) {
            console.error("Telemetry fetch error:", err);
            const errMsg =
                err.response?.data?.detail ||
                err.message ||
                "Failed to load telemetry ❌";
            setStatusMessage({ type: "danger", message: errMsg });
        } finally {
            setLoadingCompressors(false);
        }
    };

    // ── Fetch coil status ────────────────────────────────────────────────────
    const fetchCoilStatus = async () => {
        if (!machineId) return;
        setLoadingCoil(true);
        try {
            const res = await axiosInstance.get(
                `${API_BASE_URL}/hvac-coil-latest/${machineId}/`
            );
            setCoilData(res.data);
        } catch (err) {
            console.error("Coil status fetch error:", err);
        } finally {
            setLoadingCoil(false);
        }
    };

    // ── Effects ──────────────────────────────────────────────────────────────
    useEffect(() => {
        fetchTelemetry();
        fetchCoilStatus();
        const interval = setInterval(() => {
            fetchTelemetry();
            fetchCoilStatus();
        }, 60000);
        return () => clearInterval(interval);
    }, [machineId]);

    // ── Setpoint modal ───────────────────────────────────────────────────────
    const toggleModal = () => {
        setModalOpen((prev) => !prev);
        if (modalOpen) {
            setCurrentCompressor(null);
            setNewSetpoint("");
        }
    };

    const handleOpenSetpointModal = (comp) => {
        setCurrentCompressor(comp);
        setNewSetpoint(comp.bypass_set_point ?? "");
        setModalOpen(true);
    };

    const handleUpdateSetpoint = async () => {
        if (
            !currentCompressor ||
            newSetpoint === "" ||
            isNaN(Number(newSetpoint))
        ) {
            alert("Please enter a valid numeric set point.");
            return;
        }
        setIsUpdatingSetpoint(true);
        setStatusMessage({});
        const compressorId = currentCompressor.id;
        try {
            await axiosInstance.post(
                `${API_BASE_URL}/compressor/${compressorId}/setpoint/`,
                { set_point: Number(newSetpoint) }
            );
            setCompressors((prev) =>
                prev.map((c) =>
                    c.id === compressorId
                        ? { ...c, bypass_set_point: String(newSetpoint) }
                        : c
                )
            );
            setStatusMessage({
                type: "success",
                message: `Setpoint for "${currentCompressor.name}" updated to ${newSetpoint}.`,
            });
            setModalOpen(false);
            setCurrentCompressor(null);
            setNewSetpoint("");
        } catch (err) {
            console.error("Setpoint update error:", err);
            const errMsg =
                err.response?.data?.detail ||
                err.message ||
                "Failed to update setpoint.";
            setStatusMessage({ type: "danger", message: errMsg });
        } finally {
            setIsUpdatingSetpoint(false);
        }
    };

    // ── Derived ──────────────────────────────────────────────────────────────
    const runningCount = compressors.filter((c) => c.is_running).length;

    document.title = "Machine Data | HVACSYNC";

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid className="mt-1">

                    {/* ── Page Header ─────────────────────────────────────── */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                        <div>
                            <h4 className="mb-0 fw-bold">
                                {telemetry?.machine ?? "Loading..."}
                            </h4>
                            {lastUpdated && (
                                <small className="text-muted">
                                    {telemetry?.date} · {telemetry?.time}
                                    &nbsp;|&nbsp; Refreshed:{" "}
                                    {lastUpdated.toLocaleTimeString()}
                                </small>
                            )}
                        </div>
                        {!loadingCompressors && (
                            <Badge
                                color={runningCount > 0 ? "success" : "secondary"}
                                pill
                                style={{ fontSize: "12px", padding: "6px 14px" }}
                            >
                                {runningCount} / {compressors.length} Running
                            </Badge>
                        )}
                    </div>

                    {/* ── Status Alert ─────────────────────────────────────── */}
                    {statusMessage.message && (
                        <Alert color={statusMessage.type} className="mb-3">
                            {statusMessage.message}
                        </Alert>
                    )}

                    {/* ── Common Values ────────────────────────────────────── */}
                    {!loadingCompressors && telemetry?.common_values && (
                        <>
                            <h6
                                className="text-muted fw-semibold mb-2"
                                style={{
                                    fontSize: "12px",
                                    letterSpacing: "0.06em",
                                    textTransform: "uppercase",
                                }}
                            >
                                System Overview
                            </h6>
                            <Row className="mb-4">
                                {Object.entries(telemetry.common_values).map(
                                    ([key, val]) => (
                                        <CommonValueCard
                                            key={key}
                                            label={key}
                                            value={val}
                                        />
                                    )
                                )}
                            </Row>
                        </>
                    )}

                    {/* ── Compressor Cards ─────────────────────────────────── */}
                    <h6
                        className="text-muted fw-semibold mb-2"
                        style={{
                            fontSize: "12px",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}
                    >
                        Compressor Units
                    </h6>
                    <Row className="mb-4">
                        {loadingCompressors ? (
                            // Skeleton loaders
                            [...Array(4)].map((_, i) => (
                                <Col md={3} sm={6} xs={12} key={i} className="mb-3">
                                    <Card className="shadow-sm p-3">
                                        <div
                                            className="skeleton"
                                            style={{
                                                width: "100%",
                                                height: "150px",
                                                marginBottom: "10px",
                                                background: "#e9ecef",
                                                borderRadius: 6,
                                            }}
                                        />
                                        <div
                                            className="skeleton"
                                            style={{
                                                width: "60%",
                                                height: "20px",
                                                marginBottom: "8px",
                                                background: "#e9ecef",
                                                borderRadius: 4,
                                            }}
                                        />
                                        <div
                                            className="skeleton"
                                            style={{
                                                width: "40%",
                                                height: "15px",
                                                background: "#e9ecef",
                                                borderRadius: 4,
                                            }}
                                        />
                                    </Card>
                                </Col>
                            ))
                        ) : compressors.length > 0 ? (
                            compressors.map((comp) => {
                                const isRunning = comp.is_running;
                                const imageUrl = comp.image
                                    ? `https://api.hvacsync.in/media/${comp.image.replace(
                                          /\\/g,
                                          "/"
                                      )}`
                                    : "/default-compressor.png";
                                const currentVal = parseFloat(comp.current) || 0;

                                return (
                                    <Col
                                        md={3}
                                        sm={6}
                                        xs={12}
                                        key={comp.id}
                                        className="mb-3"
                                    >
                                        <Card
                                            className="shadow-sm h-100"
                                            style={{
                                                borderRadius: "10px",
                                                overflow: "hidden",
                                                border: isRunning
                                                    ? "1.5px solid #198754"
                                                    : "1.5px solid #dee2e6",
                                                transition: "border-color 0.3s",
                                            }}
                                        >
                                            {/* Running strip */}
                                            {isRunning && (
                                                <div
                                                    style={{
                                                        height: "3px",
                                                        background:
                                                            "linear-gradient(90deg, #198754, #20c997)",
                                                    }}
                                                />
                                            )}

                                            <CardBody className="d-flex align-items-center justify-content-center p-2">
                                                <img
                                                    src={imageUrl}
                                                    alt={comp.name}
                                                    style={{
                                                        width: "170px",
                                                        height: "150px",
                                                        objectFit: "contain",
                                                        opacity: isRunning ? 1 : 0.55,
                                                        transition: "opacity 0.3s",
                                                    }}
                                                />
                                            </CardBody>

                                            <CardFooter className="bg-white border-top">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <h6
                                                            className="fw-bold mb-1 text-truncate"
                                                            title={comp.name}
                                                        >
                                                            {comp.name}
                                                        </h6>

                                                        {/* Current & Bypass SP */}
                                                        <div className="d-flex gap-3 mb-1">
                                                            <div>
                                                                <small
                                                                    className="text-muted d-block"
                                                                    style={{
                                                                        fontSize: "10px",
                                                                        lineHeight: 1,
                                                                    }}
                                                                >
                                                                    CURRENT
                                                                </small>
                                                                <span
                                                                    className="fw-semibold"
                                                                    style={{
                                                                        fontSize: "14px",
                                                                        color: isRunning
                                                                            ? "#0d6efd"
                                                                            : "#6c757d",
                                                                    }}
                                                                >
                                                                    {comp.current} A
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <small
                                                                    className="text-muted d-block"
                                                                    style={{
                                                                        fontSize: "10px",
                                                                        lineHeight: 1,
                                                                    }}
                                                                >
                                                                    BYPASS SP
                                                                </small>
                                                                <span
                                                                    className="fw-semibold"
                                                                    style={{ fontSize: "14px" }}
                                                                >
                                                                    {comp.bypass_set_point}°C
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <span
                                                            className={`badge px-2 py-1 ${
                                                                isRunning
                                                                    ? "bg-success"
                                                                    : "bg-secondary"
                                                            }`}
                                                            style={{ fontSize: "11px" }}
                                                        >
                                                            {isRunning ? "Running" : "Idle"}
                                                        </span>
                                                    </div>

                                                    {/* Edit Setpoint */}
                                                    <Button
                                                        color="info"
                                                        outline
                                                        size="sm"
                                                        className="ms-2 mt-1"
                                                        onClick={() =>
                                                            handleOpenSetpointModal(comp)
                                                        }
                                                        title="Change Bypass Set Point"
                                                        disabled={isUpdatingSetpoint}
                                                    >
                                                        <FaEdit size={12} />
                                                    </Button>
                                                </div>

                                               
                                            </CardFooter>
                                        </Card>
                                    </Col>
                                );
                            })
                        ) : (
                            <Col xs={12} className="text-center text-muted py-5">
                                No compressors found for this machine.
                            </Col>
                        )}
                    </Row>

                    {/* ── Coil Register Status ─────────────────────────────── */}
                    <CoilStatusSection
                        coilStatus={coilData?.coil_status}
                        date={coilData?.date}
                        time={coilData?.time}
                        loading={loadingCoil}
                    />

                    {/* <PerformanceTrend machineId={machineId} />
                    <InputPressureChart date="2026-03-30" />
                    <CompressorTables2 /> */}

                </Container>
            </div>

            {/* ── Setpoint Modal ───────────────────────────────────────────── */}
            <Modal isOpen={modalOpen} toggle={toggleModal} centered>
                <ModalHeader toggle={toggleModal}>
                    Change Bypass Setpoint — {currentCompressor?.name}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="newSetpoint">New Bypass Set Point (°C)</Label>
                        <Input
                            id="newSetpoint"
                            type="number"
                            placeholder="e.g. 25"
                            value={newSetpoint}
                            onChange={(e) => setNewSetpoint(e.target.value)}
                            disabled={isUpdatingSetpoint}
                            autoFocus
                        />
                        <small className="form-text text-muted">
                            Current value:{" "}
                            <strong>
                                {currentCompressor?.bypass_set_point ?? "--"}°C
                            </strong>
                        </small>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={toggleModal}
                        disabled={isUpdatingSetpoint}
                    >
                        <FaTimes className="me-1" /> Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleUpdateSetpoint}
                        disabled={
                            isUpdatingSetpoint ||
                            newSetpoint === "" ||
                            isNaN(Number(newSetpoint))
                        }
                    >
                        {isUpdatingSetpoint ? (
                            <>
                                <Spinner size="sm" className="me-1" /> Saving...
                            </>
                        ) : (
                            <>
                                <FaCheck className="me-1" /> Save Setpoint
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default ChillerDetail;
